import { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSidebar } from '../contexts/SidebarContext';

type View = 'user' | 'agency';

interface UseAccountViewProps {
  userDetails: any;
  onLogout: () => void;
}

export const useAccountView = ({ userDetails, onLogout }: UseAccountViewProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeItem, setItems, setFooterItems, setActiveItem } = useSidebar();
  const initialView = (location.state as any)?.view ?? 'user';
  const [currentView, setCurrentView] = useState<View>(initialView);

  const sidebarItems = useMemo(() => {
    const canSeeAgency = userDetails?.role === 'Manager' || userDetails?.role === 'Admin' || userDetails?.role === 'Super Admin';
    const items: { id: string; label: string; icon: string; onClick: () => void }[] = [{ 
      id: 'user',     
      label: 'User Details',    
      icon: '🏠', 
      onClick: () => navigate('/Account', { state: { view: 'user' } }) 
    }];
    if (canSeeAgency) {
      items.push({ 
        id: 'agency', 
        label: 'Agency Details', 
        icon: '🏢', 
        onClick: () => navigate('/Account', { state: { view: 'agency' } }) 
      });
    }
    return items;
  }, [userDetails?.role, navigate]);

  const sidebarFooterItems = useMemo(() => [
    { id: 'help', label: 'Help', to: '/ContactUs', icon: '⁉️' },
    { id: 'privacy', label: 'Privacy Policy', to: '/PrivacyPolicy', icon: '⛓️‍💥' },
    { id: 'logout', label: 'Logout', onClick: () => { onLogout(); }, icon: '💤' },
  ], [onLogout]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!userDetails) {
      navigate('/LoginRegister', { replace: true });
    }
  }, [userDetails, navigate]);

  useLayoutEffect(() => {
    setItems(sidebarItems);
    setFooterItems(sidebarFooterItems);
    setActiveItem(initialView);
  }, [setItems, setFooterItems, setActiveItem, sidebarItems, sidebarFooterItems]);

  useEffect(() => {
    if (activeItem === 'user' || activeItem === 'agency') {
      setCurrentView(activeItem);
    }
  }, [activeItem]);

  return { currentView };
};
